import { Component, Input } from '@angular/core';
import { ComparatorService } from '../../services/comparator-service';
import { AbstractComponent } from '../../components/abstract-component/abstract.component';
import { ListLoader } from '../../components/list-loader/list-loader';
import { RootBelastungVm } from '../../../core/services/grundpfandrecht-viewmodel.service';
import {
  CapititelEntryMeta,
  PersonStammMasterListEntryMeta,
  PfandrechtMasterDetailEntryMeta,
  PfandrechtVersionDetailEntryMeta
} from '../../../core/gb-api';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DatumPipe } from '../../pipes/simple/datum.pipe';
import { TableSortModel } from '../../tablesort/tableSortModel';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { FormatConfigService } from '../../../core/services/format-config.service';
import { ErweiterungConfigResponse } from '../../../core/base-api';
import { LinkComponent, NavTarget } from '../../components/link/link.component';
import { ListLoaderWrapperComponent } from '../../components/list-loader-wrapper/list-loader-wrapper.component';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { KompaktBelegComponent } from '../../kompaktdarstellung/beleg/kompakt-beleg.component';
import { KompaktTimepointComponent } from '../../kompaktdarstellung/timepoint/kompakt-timepoint.component';
import { HistoryDirective } from '../../directives/history.directive';
import { KompaktGrundpfandrechtComponent } from '../../kompaktdarstellung/grundpfandrecht/kompakt-grundpfandrecht.component';
import { ReadMoreComponent } from '../read-more/read-more.component';
import { KompaktGrundstueckComponent } from '../../kompaktdarstellung/grundstueck/kompakt-grundstueck.component';
import { RechtsgrundComponent } from '../../kompaktdarstellung/rechtsgrund/rechtsgrund.component';
import { KompaktPersongbComponent } from '../../kompaktdarstellung/persongb/kompakt-persongb.component';
import { KompaktStandardrechtComponent } from '../../kompaktdarstellung/standardrecht/kompakt-standardrecht.component';
import { BetragPipe } from '../../pipes/simple/betrag.pipe';
import { FormatRechteIdPipe } from '../../pipes/format/format-rechte-id.pipe';
import { GlaeubigerRolleCodePipe } from '../../pipes/simple/glaeubiger-rolle-code.pipe';
import { OrderByGlaeubigerSchuldnerMasterPipe } from '../../pipes/order/order-by-glaeubigerSchuldnerMaster.pipe';
import { OrderByPfandrechtBelastungMasterPipe } from '../../pipes/order/order-by-pfandrechtBelastungMaster.pipe';
import { OrderByPfandrechtBelastungPipe } from '../../pipes/order/order-by-pfandrecht-belastung.pipe';
import { TranslateNamePipe } from '../../pipes/simple/translate-name.pipe';
import PfandtitelStatusEnum = CapititelEntryMeta.PfandtitelStatusEnum;
import ActiveErweiterungenEnum = ErweiterungConfigResponse.ActiveErweiterungenEnum;

@Component({
  selector: 'cap-grundpfandrecht-table',
  templateUrl: './grundpfandrecht-table.component.html',
  imports: [
    ListLoaderWrapperComponent,
    NgTemplateOutlet,
    NgClass,
    KompaktBelegComponent,
    KompaktTimepointComponent,
    HistoryDirective,
    KompaktGrundpfandrechtComponent,
    LinkComponent,
    ReadMoreComponent,
    KompaktGrundstueckComponent,
    RechtsgrundComponent,
    KompaktPersongbComponent,
    TranslatePipe,
    KompaktStandardrechtComponent,
    BetragPipe,
    DatumPipe,
    FormatRechteIdPipe,
    GlaeubigerRolleCodePipe,
    OrderByGlaeubigerSchuldnerMasterPipe,
    OrderByPfandrechtBelastungMasterPipe,
    OrderByPfandrechtBelastungPipe,
    TranslateNamePipe
  ]
})
export class GrundpfandrechtTableComponent extends AbstractComponent {
  @Input()
  hasRight: boolean = true;

  private _listLoader: ListLoader<RootBelastungVm>;
  private sortModel: TableSortModel<RootBelastungVm>;
  isGeschaeftsmodulCapWebErweiterung: boolean;
  navTargetGrundstuecke: NavTarget = NavTarget.NAV_GRUNDSTUECK;

  constructor(
    protected translateService: TranslateService,
    private datumPipe: DatumPipe,
    protected comparatorService: ComparatorService,
    private appFormatConfigService: FormatConfigService
  ) {
    super();
    keysToTranslate<string>([
      'Pfandstelle',
      'DatumOderBelegOderEntstehung',
      'ID-DatumOderBelegOderRechtsgrund',
      'glaeubiger',
      'Nachrueckungsrecht',
      'Mit',
      'Gruppe',
      'detailPfandrechtGlaeubiger',
      'gehoertZu'
    ]);
    this.sortModel = new TableSortModel('table', this.comparatorService);
    this.sortModel.addColumn('dummyColumn', this.comparatorService.pfandrechtBelastungComparator).switchSortDirection();
    this.addSub(
      this.appFormatConfigService.erweiterungsConfig$.subscribe((config) => {
        this.isGeschaeftsmodulCapWebErweiterung = config?.activeErweiterungen.includes(ActiveErweiterungenEnum.GESCHAEFTSMODUL_CAP_WEB);
      })
    );
  }

  @Input()
  set listLoader(value: ListLoader<RootBelastungVm>) {
    if (!value) {
      return;
    }
    this._listLoader = value;
    this._listLoader.applySortModel(this.sortModel);
  }

  get listLoader(): ListLoader<RootBelastungVm> {
    return this._listLoader;
  }

  getQueryParams(master: PfandrechtMasterDetailEntryMeta, version: PfandrechtVersionDetailEntryMeta) {
    return { id: master.id.id, versionId: version.id.id, histEnum: version.histInfo.histEnum };
  }

  getTiteleingang(titel: CapititelEntryMeta): string {
    let text: string;
    const anmelder: string = this.getAnmelder(titel.anmelder);
    const eintragdatum = this.datumPipe.transform(titel.eintragDatum);
    if (titel.pfandtitelStatus === PfandtitelStatusEnum.ERSTELLT || titel.pfandtitelStatus === PfandtitelStatusEnum.EINGETRAGEN) {
      if (anmelder) {
        text = this.translateService.instant('JaAmDatumDurchAnmelder', { datum: eintragdatum, anmelder: anmelder });
      } else {
        text = this.translateService.instant('JA');
      }
    } else {
      text = this.translateService.instant('NEIN');
    }
    return text;
  }

  private getAnmelder(anmelder: Array<PersonStammMasterListEntryMeta>): string {
    return anmelder
      .map((a): string => {
        const vorname: string = a.version.vorname;
        const sitz: string = a.version.sitz;
        const name: string[] = vorname ? [a.version.name, vorname] : [a.version.name];
        if (sitz) {
          return [name.join(' '), sitz].join(', ');
        } else {
          return name.join(' ');
        }
      })
      .join(', ');
  }
}
